import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Sparkles, Plus, Pencil, Trash2, CheckCircle2, Play, Ruler, TrendingUp, SlidersHorizontal } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import CustomMetricsDialog from "@/components/CustomMetricsDialog";
import { SafeHCaptcha } from "@/components/SafeHCaptcha";
import { trackOnboardingEvent } from "@/lib/onboardingAnalytics";
import {
  incrementRateLimit,
  PreAuthMetric,
  savePreAuthMetrics,
  clearPreAuthMetrics,
  getDeviceId
} from "@/lib/preAuthMetrics";
import { getAllMetrics } from "@/lib/metricDefinitions";
import { AddMetricDialog } from "@/components/AddMetricDialog";
import { MetricsTour } from "@/components/auth/mobile/MetricsTour";
import { useTheme } from "@/providers/ThemeProvider";
import { getLocalizedMetricName } from "@/lib/utils";

const HCAPTCHA_SITE_KEY = "eebe1a6c-3fc4-42f4-9df4-6bee9822d538";

interface OnboardingMetricsPageProps {
  userInput: string;
  onConfirm: (metrics: PreAuthMetric[]) => void;
  onBack: () => void;
  preGeneratedMetrics?: PreAuthMetric[];
  skipThemeSelectorAndTour?: boolean; // Skip theme/tour if coming from tutorial
  cameFromTour?: boolean; // If true, user already saw OnboardingTutorial (from /tour or quick tour)
}

interface GeneratedMetric {
  key: string;
  name: string;
  emoji: string;
  unit: string;
  type: "numeric" | "boolean";
  goal?: number;
  value: number;
  inverse: boolean;
  isAdditive: boolean; // true = counter/additive, false = snapshot
  isRelative?: boolean;
}

export const OnboardingMetricsPage = ({ userInput, onConfirm, onBack, preGeneratedMetrics, skipThemeSelectorAndTour = false, cameFromTour = false }: OnboardingMetricsPageProps) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [metrics, setMetrics] = useState<GeneratedMetric[]>(
    preGeneratedMetrics?.map(m => ({
      ...m,
      value: 0, // Add default value for GeneratedMetric type
      isAdditive: true // Default to true for pre-generated metrics
    })) || []
  );
  const [error, setError] = useState<string | null>(null);
  const [editingMetric, setEditingMetric] = useState<GeneratedMetric | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showCreateCustomDialog, setShowCreateCustomDialog] = useState(false);
  const [allMetrics, setAllMetrics] = useState<any[]>([]);
  const [editingGoalKey, setEditingGoalKey] = useState<string | null>(null);
  const [goalInputValue, setGoalInputValue] = useState<string>("");
  const [hcaptchaToken, setHcaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<any>(null);
  const [captchaRetryCount, setCaptchaRetryCount] = useState(0);
  const [loadingPhraseIndex, setLoadingPhraseIndex] = useState(0);
  const [showThemeSelector, setShowThemeSelector] = useState(false);
  const [showMetricsTour, setShowMetricsTour] = useState(false);

  // Fun phrases that cycle while AI analyzes the input
  const { t } = useTranslation();
  // Fun phrases that cycle while AI analyzes the input
  const loadingPhrases = (t('metrics.loading_phrases', { returnObjects: true }) as string[]) || ["Just a sec..."];

  useEffect(() => {
    loadMetrics();
  }, []);

  // Show theme selector and tour when we have pre-generated metrics
  useEffect(() => {
    if (preGeneratedMetrics && preGeneratedMetrics.length > 0) {
      setTimeout(() => {
        setShowThemeSelector(true);
      }, 800);
    }
  }, [preGeneratedMetrics]);

  // Cycle through loading phrases while loading
  useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => {
        setLoadingPhraseIndex(prev => (prev + 1) % loadingPhrases.length);
      }, 2000); // Change every 2 seconds
      return () => clearTimeout(timer);
    } else {
      setLoadingPhraseIndex(0); // Reset when not loading
    }
  }, [loading, loadingPhraseIndex]);

  const loadMetrics = async () => {
    const metrics = await getAllMetrics();
    setAllMetrics(Array.isArray(metrics) ? metrics : []);
  };

  const handleCaptchaVerify = (token: string) => {
    console.log('[OnboardingMetrics] hCaptcha verified, token received');
    setHcaptchaToken(token);
    // Auto-generate metrics once we have the token (only if not pre-generated)
    if (!preGeneratedMetrics || preGeneratedMetrics.length === 0) {
      generateMetrics(token);
    }
  };

  const handleCaptchaError = (err: string) => {
    console.error('[OnboardingMetrics] hCaptcha error:', err);
    // Auto-retry captcha — it must not be skippable
    if (captchaRetryCount < 5) {
      console.log(`[OnboardingMetrics] Retrying captcha (${captchaRetryCount + 1}/5)...`);
      setCaptchaRetryCount(prev => prev + 1);
      setTimeout(() => {
        if (captchaRef.current) {
          try {
            if (typeof captchaRef.current.resetCaptcha === 'function') {
              captchaRef.current.resetCaptcha();
            }
            if (typeof captchaRef.current.execute === 'function') {
              captchaRef.current.execute();
            }
          } catch (e) {
            console.error('[OnboardingMetrics] Captcha re-execute failed:', e);
          }
        }
      }, 2000);
    } else {
      setLoading(false);
      setError(t('metrics.captcha_error') || 'Security check failed. Please try again.');
    }
  };

  const handleCaptchaLoad = () => {
    console.log('[OnboardingMetrics] hCaptcha loaded, executing...');
    // Skip captcha if we already have pre-generated metrics
    if (preGeneratedMetrics && preGeneratedMetrics.length > 0) {
      return;
    }

    if (captchaRef.current) {
      // Store ref in closure to ensure it's available
      const captchaInstance = captchaRef.current;

      // Use requestIdleCallback to defer execution and not block the UI
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          try {
            // Check if ref is still valid and execute method exists
            if (captchaRef.current && captchaInstance && typeof captchaInstance.execute === 'function') {
              captchaInstance.execute();
            }
          } catch (error) {
            console.error('[OnboardingMetrics] hCaptcha execute error:', error);
          }
        }, { timeout: 100 });
      } else {
        // Fallback for browsers without requestIdleCallback
        setTimeout(() => {
          try {
            if (captchaRef.current && captchaInstance && typeof captchaInstance.execute === 'function') {
              captchaInstance.execute();
            }
          } catch (error) {
            console.error('[OnboardingMetrics] hCaptcha execute error:', error);
          }
        }, 50);
      }
    }
  };

  const generateMetrics = async (captchaToken?: string) => {
    const token = captchaToken || hcaptchaToken;

    if (!token) {
      setError("Please complete the security check");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Increment rate limit counter
      incrementRateLimit();

      // Get all available metrics for the AI to work with
      const allMetrics = await getAllMetrics();
      const enabledMetrics = (Array.isArray(allMetrics) ? allMetrics : []).map(m => ({
        key: m.key,
        name: m.name,
        emoji: m.emoji,
        unit: m.unit || "",
      }));

      // Get device ID for rate limiting
      const deviceId = getDeviceId();

      // Call parse-quick-entry without auth
      const response = await supabase.functions.invoke("parse-quick-entry", {
        body: {
          text: userInput,
          enabledMetrics,
          anonymous: true, // Flag for anonymous/onboarding flow
          device_id: deviceId, // Include device ID for rate limiting
          hcaptcha_token: token, // Include hCaptcha token
        },
      });

      if (response.error) {
        console.error("Parse error:", response.error);
        console.log("[OnboardingMetrics] Edge function error, navigating to auth signup");
        navigate("/auth?signup=true&from=onboarding");
        return;
      }

      if (!response.data || !response.data.metrics) {
        console.log("[OnboardingMetrics] No metrics returned, navigating to auth signup");
        navigate("/auth?signup=true&from=onboarding");
        return;
      }

      // Transform the response into our format
      // Track custom metric slots to assign proper slot-based keys
      const usedSlots = new Set<string>();
      const availableSlots = Array.from({ length: 10 }, (_, i) => `custom_${i + 1}`);

      const generatedMetrics: GeneratedMetric[] = response.data.metrics
        .filter((m: any) => m.value !== undefined)
        .map((m: any) => {
          // Check if this is a built-in metric
          const isBuiltIn = m.key && allMetrics.some(builtin => builtin.key === m.key);

          let metricKey: string;

          if (isBuiltIn) {
            // Use the built-in key as-is
            metricKey = m.key;
          } else {
            // This is a custom metric - assign a slot-based key
            const nextSlot = availableSlots.find(slot => !usedSlots.has(slot));
            if (nextSlot) {
              metricKey = nextSlot;
              usedSlots.add(nextSlot);
              console.log(`[OnboardingMetrics] Assigned ${nextSlot} to custom metric: ${m.name}`);
            } else {
              console.warn(`[OnboardingMetrics] No available slots for custom metric: ${m.name}`);
              return null; // Skip this metric if no slots available
            }
          }

          return {
            key: metricKey,
            name: m.name,
            emoji: m.emoji || "⭐",
            unit: m.unit || "",
            type: m.type || "numeric",
            goal: m.goal, // Keep undefined if not provided (rating metrics)
            value: m.value || 0,
            inverse: m.inverse || false,
            isAdditive: m.isAdditive !== false, // Default to true if not specified
            isRelative: m.isRelative || m.is_relative || false,
          };
        })
        .filter((m): m is GeneratedMetric => m !== null); // Remove any nulls

      if (generatedMetrics.length === 0) {
        throw new Error("Couldn't understand that. Try being more specific!");
      }

      setMetrics(generatedMetrics);

      // Track successful metrics generation
      trackOnboardingEvent({
        eventType: "metrics_generated",
        metadata: { number_of_metrics: generatedMetrics.length },
      });

      // Show theme selector first, then tour
      setTimeout(() => {
        setShowThemeSelector(true);
      }, 800);
    } catch (err: any) {
      console.error("Error generating metrics:", err);
      console.log("[OnboardingMetrics] Error caught, navigating to auth signup");

      // Track captcha failure if it's a captcha-related error
      if (err.message?.toLowerCase().includes('captcha') ||
        err.message?.toLowerCase().includes('verification')) {
        trackOnboardingEvent({
          eventType: "captcha_failed",
          metadata: { error_message: err.message },
        });
      }

      // Navigate to auth signup instead of showing error screen
      navigate("/auth?signup=true&from=onboarding");
    } finally {
      setLoading(false);
    }
  };

  const handleEditMetric = (metric: GeneratedMetric) => {
    // Only allow full editing for custom metrics (name, emoji, unit, goal, inverse)
    if (metric.key.startsWith('custom_')) {
      setEditingMetric(metric);
    } else {
      // For built-in metrics, show error
      toast.error(t('notifications.toast.built_in_metrics_cannot_be_fully_edited_you_can_ad'));
    }
  };

  const handleEditGoal = (metric: GeneratedMetric) => {
    setEditingGoalKey(metric.key);
    setGoalInputValue(String(metric.goal));
  };

  const handleSaveGoal = (key: string) => {
    const newGoal = parseFloat(goalInputValue);
    if (isNaN(newGoal) || newGoal < 0) {
      toast.error(t('notifications.toast.please_enter_a_valid_goal'));
      return;
    }

    setMetrics(metrics.map(m =>
      m.key === key ? { ...m, goal: newGoal } : m
    ));
    setEditingGoalKey(null);
  };

  const handleCancelGoalEdit = () => {
    setEditingGoalKey(null);
    setGoalInputValue("");
  };

  const handleSaveEdit = (editedValues: {
    name: string;
    emoji: string;
    unit: string;
    goal: string;
    inverse: boolean;
    metricType: "numeric" | "boolean";
    isAdditive: boolean;
    isRelative?: boolean;
  }) => {
    if (!editingMetric) return;

    // Parse goal value and handle edge cases
    let goalValue: number | undefined;
    if (editedValues.isRelative || editedValues.metricType === "boolean") {
      goalValue = undefined;
    } else {
      const parsedGoal = parseFloat(editedValues.goal);
      goalValue = isNaN(parsedGoal) ? undefined : parsedGoal;
    }

    const updatedMetrics = metrics.map((m) =>
      m.key === editingMetric.key
        ? {
          ...m,
          name: editedValues.name,
          emoji: editedValues.emoji,
          unit: editedValues.unit,
          goal: goalValue,
          inverse: editedValues.inverse,
          type: editedValues.metricType,
          isAdditive: editedValues.isAdditive,
          isRelative: editedValues.isRelative ?? false,
        }
        : m
    );

    setMetrics(updatedMetrics);
    setEditingMetric(null);
  };

  const handleAddBuiltInMetric = (metricKey: string) => {
    // Find the metric definition
    if (!Array.isArray(allMetrics)) {
      toast.error(t('notifications.toast.metrics_not_loaded_yet'));
      return;
    }
    const metricDef = allMetrics.find(m => m.key === metricKey);
    if (!metricDef) {
      toast.error(t('notifications.toast.metric_not_found'));
      return;
    }

    // Add to metrics list with localized name
    const newMetric: GeneratedMetric = {
      key: metricKey,
      name: getLocalizedMetricName(metricKey, t, metricDef.name),
      emoji: metricDef.emoji || "⭐",
      unit: metricDef.unit || "",
      type: metricDef.type || "numeric",
      goal: metricDef.defaultGoal, // Keep undefined for rating metrics
      value: 0,
      inverse: metricDef.isInverse || false,
      isAdditive: true, // Built-in metrics default to additive
    };

    setMetrics([...metrics, newMetric]);
    setShowAddDialog(false);
  };

  const handleCreateCustomMetric = (newMetric: { key: string; name: string; emoji: string; unit: string; type: "numeric" | "boolean"; goal: number; value: number; inverse: boolean; isAdditive?: boolean; isRelative?: boolean; }) => {
    const metricWithDefaults: GeneratedMetric = {
      ...newMetric,
      isAdditive: newMetric.isAdditive ?? true, // Default to true if not specified
    };
    setMetrics([...metrics, metricWithDefaults]);
    setShowCreateCustomDialog(false);
    setShowAddDialog(false);
  };

  const handleDeleteMetric = (key: string) => {
    setMetrics(metrics.filter((m) => m.key !== key));
  };

  const handleThemeSelected = () => {
    // Mark that theme was chosen during onboarding
    localStorage.setItem('gitfit-theme-chosen', 'true');
    setShowThemeSelector(false);
  };



  const handleConfirm = () => {
    // Convert to PreAuthMetric format
    const preAuthMetrics: PreAuthMetric[] = metrics.map((m) => ({
      key: m.key,
      name: m.name,
      emoji: m.emoji,
      unit: m.unit,
      type: m.type,
      goal: m.goal || 0, // Use 0 for metrics without goals
      inverse: m.inverse,
      isRelative: m.isRelative,
      created_at: new Date().toISOString(),
    }));

    // Always save to localStorage automatically
    savePreAuthMetrics(preAuthMetrics);
    console.log("[OnboardingMetrics] Saved metrics to localStorage:", preAuthMetrics);

    // Continue to next step (which navigates to auth page)
    onConfirm(preAuthMetrics);
  };

  // Invisible hCaptcha renders in background
  const captchaComponent = !hcaptchaToken && (
    <SafeHCaptcha
      sitekey={HCAPTCHA_SITE_KEY}
      onVerify={handleCaptchaVerify}
      onLoad={handleCaptchaLoad}
      onError={handleCaptchaError}
      ref={captchaRef}
      size="invisible"
      loadAsync={true}
    />
  );

  // Only show loading if we don't have pre-generated metrics
  if ((loading || !hcaptchaToken) && (!preGeneratedMetrics || preGeneratedMetrics.length === 0)) {
    // Show loading state while captcha verifies or metrics generate
    return (
      <div className="h-screen bg-background flex flex-col items-center justify-center p-6 overflow-hidden">
        {captchaComponent}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="text-center space-y-6"
        >
          {/* Glowing spinner */}
          <motion.div
            className="relative mx-auto w-20 h-20"
            animate={{
              scale: [1, 1.05, 1],
            }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            {/* Outer glow ring */}
            <motion.div
              className="absolute inset-0 rounded-full bg-primary/30 blur-xl"
              animate={{
                scale: [1, 1.3, 1],
                opacity: [0.5, 0.8, 0.5],
              }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            />

            {/* Spinning ring */}
            <motion.div
              className="absolute inset-2 rounded-full border-2 border-primary/40 border-t-primary"
              animate={{ rotate: 360 }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            />

            {/* Center icon */}
            <div className="absolute inset-0 flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-primary" />
            </div>
          </motion.div>

          {/* Text content */}
          <div className="space-y-2">
            <h2 className="text-xl font-bold text-foreground">
              {t('metrics.generating')}
            </h2>
            <div className="h-5 relative flex items-center justify-center">
              <AnimatePresence mode="wait">
                <motion.p
                  key={loadingPhraseIndex}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.4 }}
                  className="text-sm text-muted-foreground absolute"
                >
                  {loadingPhrases[loadingPhraseIndex]}
                </motion.p>
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen bg-background flex flex-col items-center justify-center p-6 overflow-hidden">
        {/* Re-render captcha so retry can use it */}
        <SafeHCaptcha
          sitekey={HCAPTCHA_SITE_KEY}
          onVerify={handleCaptchaVerify}
          onLoad={handleCaptchaLoad}
          onError={handleCaptchaError}
          ref={captchaRef}
          size="invisible"
          loadAsync={true}
        />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md space-y-6 text-center"
        >
          <h2 className="text-2xl font-bold text-destructive">{t('metrics.oops')}</h2>
          <p className="text-muted-foreground">{error}</p>
          <Button
            onClick={() => {
              setError(null);
              setCaptchaRetryCount(0);
              setHcaptchaToken(null);
              // Re-execute captcha
              setTimeout(() => {
                if (captchaRef.current && typeof captchaRef.current.execute === 'function') {
                  try {
                    if (typeof captchaRef.current.resetCaptcha === 'function') {
                      captchaRef.current.resetCaptcha();
                    }
                    captchaRef.current.execute();
                  } catch (e) {
                    console.error('[OnboardingMetrics] Manual retry failed:', e);
                  }
                }
              }, 500);
            }}
            className="w-full"
          >
            {t('metrics.try_again') || 'Try Again'}
          </Button>
          <Button onClick={onBack} variant="ghost" className="w-full">
            ← {t('metrics.back')}
          </Button>
        </motion.div>
      </div>
    );
  }

  // Show verifying screen while waiting for captcha (skip if we have pre-generated metrics)
  if (!hcaptchaToken && !loading && (!preGeneratedMetrics || preGeneratedMetrics.length === 0)) {
    return (
      <div className="h-screen bg-background flex flex-col items-center justify-center p-6 overflow-hidden">
        {/* Invisible hCaptcha */}
        <SafeHCaptcha
          sitekey={HCAPTCHA_SITE_KEY}
          onVerify={handleCaptchaVerify}
          onLoad={handleCaptchaLoad}
          onError={handleCaptchaError}
          ref={captchaRef}
          size="invisible"
          loadAsync={true}
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md space-y-6 text-center"
        >
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-foreground">{t('metrics.verifying')}</h2>
            <p className="text-sm text-muted-foreground">
              {t('metrics.verifying_sub')}
            </p>
          </div>

          <Button onClick={onBack} variant="ghost" className="w-full">
            ← {t('metrics.back')}
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-background flex flex-col items-center overflow-hidden">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="flex flex-col h-full w-full max-w-md"
      >
        {/* Header - With proper spacing */}
        <motion.div
          className="flex-shrink-0 pt-8 pb-4 px-6"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <h1 className="text-lg font-bold flex items-center justify-center gap-2 text-foreground">
            <Sparkles className="w-5 h-5 text-primary" />
            {t('metrics.header_title')}
          </h1>
          <p className="text-sm text-muted-foreground text-center mt-1">
            {t('metrics.header_desc')}
          </p>
        </motion.div>

        {/* Metrics List - Properly Scrollable */}
        <div className="flex-1 overflow-y-auto min-h-0 px-6">
          <div className="space-y-3 pb-6">
            <AnimatePresence mode="popLayout">
              {metrics.map((metric, index) => (
                <motion.div
                  key={metric.key}
                  initial={{ opacity: 0, y: 20, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9, y: -10 }}
                  transition={{
                    delay: index * 0.08,
                    duration: 0.35,
                    ease: [0.25, 0.46, 0.45, 0.94]
                  }}
                >
                  <Card className="p-4 relative">
                    <div className="flex items-start gap-3">
                      <div className="text-2xl flex-shrink-0 leading-none">{metric.emoji}</div>
                      <div className="flex-1 min-w-0 space-y-1.5">
                        <div className="flex items-center gap-1.5">
                          <h3 className="font-semibold text-base leading-tight">{metric.name}</h3>
                          {metric.type !== "boolean" && metric.key.startsWith('custom_') && (
                            <button
                              onClick={() => {
                                const isAdditive = metric.isAdditive !== false;
                                if (isAdditive) {
                                  toast.info(t('notifications.toast.counter_metric'), {
                                    description: t('notifications.toast.counter_metric_description')
                                  });
                                } else {
                                  toast.info(t('notifications.toast.snapshot_metric'), {
                                    description: t('notifications.toast.snapshot_metric_description')
                                  });
                                }
                              }}
                              className="flex-shrink-0 text-muted-foreground/40 hover:text-muted-foreground/70 transition-colors"
                            >
                              {metric.isAdditive !== false ? (
                                <TrendingUp className="w-3.5 h-3.5" />
                              ) : (
                                <Ruler className="w-3.5 h-3.5" />
                              )}
                            </button>
                          )}
                        </div>
                        {metric.type === "numeric" && typeof metric.goal === "number" && !isNaN(metric.goal) && !metric.isRelative ? (
                          <div className="flex items-center gap-2 flex-wrap">
                            {editingGoalKey === metric.key ? (
                              <>
                                <input
                                  type="number"
                                  value={goalInputValue}
                                  onChange={(e) => setGoalInputValue(e.target.value)}
                                  className="w-16 px-2 py-1 text-xs border rounded bg-background"
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveGoal(metric.key);
                                    if (e.key === 'Escape') handleCancelGoalEdit();
                                  }}
                                />
                                <span className="text-xs text-muted-foreground">{metric.unit}</span>
                                <Button
                                  size="sm"
                                  onClick={() => handleSaveGoal(metric.key)}
                                  className="h-6 px-2 text-xs"
                                >
                                  {t('ui.text.save')}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={handleCancelGoalEdit}
                                  className="h-6 px-2 text-xs"
                                >
                                  {t('ui.text.cancel')}
                                </Button>
                              </>
                            ) : (
                              <>
                                <p className="text-xs text-muted-foreground">
                                  {t('ui.text.out_of_goal', { goal: metric.goal, unit: metric.unit })}
                                </p>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleEditGoal(metric)}
                                  className="h-5 w-5"
                                >
                                  <Pencil className="w-3 h-3" />
                                </Button>
                              </>
                            )}
                          </div>
                        ) : metric.type === "boolean" ? (
                          <p className="text-xs text-muted-foreground">{t('ui.text.track_yes_no')}</p>
                        ) : (
                          <p className="text-xs text-muted-foreground">
                            {metric.unit}
                          </p>
                        )}
                        {/* Calories gets an editable checkbox, other inverse metrics get static text */}
                        {metric.key === 'calories' ? (
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id={`inverse-${metric.key}`}
                              checked={metric.inverse}
                              onCheckedChange={(checked) => {
                                setMetrics(prev => prev.map(m =>
                                  m.key === 'calories' ? { ...m, inverse: !!checked } : m
                                ));
                              }}
                            />
                            <label
                              htmlFor={`inverse-${metric.key}`}
                              className="text-xs text-amber-600 dark:text-amber-400 cursor-pointer"
                            >
                              {t('ui.text.above_is_bad')}
                            </label>
                          </div>
                        ) : metric.inverse && (
                          <p className="text-xs text-amber-600 dark:text-amber-400">
                            {metric.type === "boolean" ? t('ui.text.yes_is_bad') : t('ui.text.above_is_bad')}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        {metric.key.startsWith('custom_') && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditMetric(metric)}
                            className="h-7 w-7"
                          >
                            <SlidersHorizontal className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteMetric(metric.key)}
                          className="h-7 w-7 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                    {!metric.key.startsWith('custom_') && (
                      <div className="absolute bottom-2 right-2 text-[10px] text-muted-foreground/30 font-medium">{t('ui.text.built_in')}</div>
                    )}
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Add Metric Button */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: metrics.length * 0.08 + 0.2, duration: 0.3 }}
            >
              <Button
                variant="outline"
                onClick={() => setShowAddDialog(true)}
                className="w-full border-dashed h-12 text-sm mt-2"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('metrics.create')}
              </Button>
            </motion.div>
          </div>
        </div>

        {/* Confirm Button - Fixed at bottom */}
        <motion.div
          className="flex-shrink-0 px-6 pt-6 pb-10 border-t border-border/50 bg-background/80 backdrop-blur-sm"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          <div className="px-2 space-y-3">
            <button
              onClick={() => {
                // Clear in-memory metrics
                setMetrics([]);
                // Clear all pre-auth metrics from localStorage
                clearPreAuthMetrics();
                console.log("[OnboardingMetrics] Cleared all pre-auth metrics (in-memory and localStorage)");
                // Navigate to auth page without saving metrics
                navigate("/auth?signup=true&from=onboarding");
              }}
              className="w-full text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors flex items-center justify-center gap-1 mb-2"
            >
              {t('metrics.start_fresh')}
            </button>
            <button
              onClick={() => setShowMetricsTour(true)}
              className="w-full text-[10px] text-muted-foreground/40 hover:text-muted-foreground/60 transition-colors flex items-center justify-center gap-1 mb-3"
            >
              {t('metrics.guide_nerds')}
            </button>
            <Button
              onClick={handleConfirm}
              disabled={metrics.length === 0}
              className="w-full h-12 text-base font-semibold rounded-full shadow-[0_0_30px_-5px_hsl(var(--primary)/0.6)] hover:shadow-[0_0_30px_-5px_hsl(var(--primary)/0.8)] transition-all duration-300 hover:scale-[1.02] bg-gradient-to-r from-primary to-primary/80"
              size="lg"
            >
              <CheckCircle2 className="w-5 h-5 mr-2" />
              {t('metrics.save_continue')}
            </Button>
          </div>
        </motion.div>
      </motion.div>

      {/* Metrics Tour */}
      <MetricsTour
        isOpen={showMetricsTour}
        onClose={() => setShowMetricsTour(false)}
        metrics={metrics}
      />

      {/* Edit Dialog - Only for custom metrics */}
      {editingMetric && editingMetric.key.startsWith('custom_') && (
        <CustomMetricsDialog
          userId="" // No user ID for anonymous onboarding
          open={!!editingMetric}
          onOpenChange={(open) => {
            if (!open) setEditingMetric(null);
          }}
          editMode={true}
          initialValues={{
            name: editingMetric.name,
            emoji: editingMetric.emoji,
            unit: editingMetric.unit,
            goal: editingMetric.goal !== undefined ? String(editingMetric.goal) : "",
            inverse: editingMetric.inverse,
            metricType: editingMetric.type,
            isAdditive: editingMetric.isAdditive,
            isRelative: editingMetric.isRelative ?? false,
          }}
          onSaveEdit={handleSaveEdit}
          hideColorPicker={true} // Hide color picker during anonymous onboarding
        />
      )}

      {/* Add Metric Dialog - Choose built-in or create custom */}
      <AddMetricDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        userId="" // No user ID for anonymous
        allMetrics={allMetrics}
        metricPreferences={{}} // No preferences for anonymous
        customMetrics={metrics.filter(m => m.key.startsWith('custom_')).map(m => ({
          id: m.key,
          user_id: '',
          metric_key: m.key,
          metric_name: m.name,
          metric_emoji: m.emoji,
          metric_unit: m.unit,
          metric_type: m.type,
          is_inverse: m.inverse,
          created_at: new Date().toISOString(),
        }))}
        displayedMetricKeys={new Set(metrics.map(m => m.key))}
        manuallyAddedMetrics={[]}
        onAddBuiltInMetric={handleAddBuiltInMetric}
        onAddExistingCustomMetric={() => { }} // Not applicable for anonymous
        onCreateCustomMetric={handleCreateCustomMetric}
        hideColorPicker={true} // Hide color picker during anonymous onboarding
      />

      {/* Theme Selector */}
      <ThemeSelectorModal
        isOpen={showThemeSelector}
        onComplete={handleThemeSelected}
      />
    </div>
  );
};

// Theme Selector Modal Component
const ThemeSelectorModal = ({ isOpen, onComplete }: { isOpen: boolean; onComplete: () => void }) => {
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation();
  const [showNext, setShowNext] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setShowNext(true), 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100]"
          />

          {/* Theme Selector Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed inset-x-4 top-8 bottom-8 max-w-lg mx-auto bg-background rounded-2xl shadow-2xl z-[101] overflow-hidden flex flex-col"
          >
            <div className="flex-1 flex items-center justify-center p-6">
              <div className="w-full space-y-8">
                <div className="text-center space-y-2">
                  <h2 className="text-3xl font-bold">{t('metrics.choose_theme')}</h2>
                  <p className="text-muted-foreground">{t('metrics.pick_appearance')}</p>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div
                    className="space-y-2 cursor-pointer group"
                    onClick={() => setTheme('light')}
                  >
                    <div className={`relative rounded-[2rem] overflow-hidden border-4 transition-all duration-300 ${theme === 'light'
                        ? "border-green-500 shadow-[0_0_20px_rgba(34,197,94,0.3)] scale-105"
                        : "border-transparent hover:scale-105 opacity-70 hover:opacity-100"
                      }`}>
                      <img
                        src="/iphone-screenshots/light-mode.png"
                        alt={t('accessibility.labels.light_mode')}
                        className="w-full h-auto object-cover"
                      />
                    </div>
                    <p className={`text-center text-sm font-medium transition-colors ${theme === 'light' ? "text-foreground" : "text-muted-foreground"
                      }`}>{t('metrics.light')}</p>
                  </div>

                  <div
                    className="space-y-2 cursor-pointer group"
                    onClick={() => setTheme('system')}
                  >
                    <div className={`relative rounded-[2rem] overflow-hidden border-4 transition-all duration-300 ${theme === 'system'
                        ? "border-green-500 shadow-[0_0_20px_rgba(34,197,94,0.3)] scale-105"
                        : "border-transparent hover:scale-105 opacity-70 hover:opacity-100"
                      }`}>
                      <img
                        src="/iphone-screenshots/system-mode.png"
                        alt={t('accessibility.labels.system_mode')}
                        className="w-full h-auto object-cover"
                      />
                    </div>
                    <p className={`text-center text-sm font-medium transition-colors ${theme === 'system' ? "text-foreground" : "text-muted-foreground"
                      }`}>{t('metrics.system')}</p>
                  </div>

                  <div
                    className="space-y-2 cursor-pointer group"
                    onClick={() => setTheme('dark')}
                  >
                    <div className={`relative rounded-[2rem] overflow-hidden border-4 transition-all duration-300 ${theme === 'dark'
                        ? "border-green-500 shadow-[0_0_20px_rgba(34,197,94,0.3)] scale-105"
                        : "border-transparent hover:scale-105 opacity-70 hover:opacity-100"
                      }`}>
                      <img
                        src="/iphone-screenshots/dark-mode.png"
                        alt={t('accessibility.labels.dark_mode')}
                        className="w-full h-auto object-cover"
                      />
                    </div>
                    <p className={`text-center text-sm font-medium transition-colors ${theme === 'dark' ? "text-foreground" : "text-muted-foreground"
                      }`}>{t('metrics.dark')}</p>
                  </div>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{
                    opacity: showNext ? 1 : 0,
                    y: showNext ? 0 : 20
                  }}
                  transition={{ duration: 0.5 }}
                  className="flex justify-center"
                >
                  <Button
                    size="lg"
                    className="w-full max-w-[200px]"
                    onClick={onComplete}
                  >
                    {t('metrics.next')}
                  </Button>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
